import React, { useContext, useCallback, useState, useEffect } from "react";
import { Box, Button, Grid, Checkbox, Stack } from "@mui/material";
import { MyDialog } from "../../component/index";
import theme from "../../styles/theme";
import { useDropzone } from "react-dropzone";
import DataSourceContext from "../../context/DataSourceMngContext";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import { Typography, FormControl, Select, MenuItem, FormControlLabel } from "@mui/material";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import IconButton from "@mui/material/IconButton";
import MyAutocomplete from "../../component/input/autocomplete";
import DatePickerYearMonth from "../../component/input/date-picker";
import { CustomChip } from "../../styles/style";
import DescriptionIcon from '@mui/icons-material/Description';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import Switch from '@mui/material/Switch';

const General_model = [
  {
    id: 1,
    name: "General Model",
    value: "general",
  },
  {
    id: 2,
    name: "g2.s",
    value: "g2-s"
  },
  {
    id: 3,
    name: "g2.m",
    value: "g2-m"
  },
  {
    id: 4,
    name: "Fleetcard Model",
    value: "fleetcard",
  },
  {
    id: 5,
    name: "Electricity Model",
    value: "electricity",
  },
  {
    id: 6,
    name: "Water Model",
    value: "water",
  }
]

const Water_meter_model = [
  {
    id: 1,
    name: "Water Meter",
    value: "water_meter",
  }
]

const Waste_model = [
  {
    id: 1,
    name: "Waste",
    value: "xlsx-csv-waste",
  }
]

const Commuting_model = [
  {
    id: 1,
    name: "Commuting",
    value: "xlsx-csv-travelling",
  }
]

export default function DialogUploadTransaction() {
  const { dialog, loading } = useContext(DataSourceContext);

  return (
    <MyDialog
      Header={`Upload File(s)`}
      textConfirm="Save"
      maxHeight={600}
      minWidth={600}
      open={dialog.upload_transaction}
      loading={loading.modal}
      actionContent={false}
      customStyle={{
        "& .MuiPaper-root": {
          maxHeight: 600,
          minWidth: 600,
          backgroundColor: "#F5F5F5",
        },
      }}
    >
      <Contents />
    </MyDialog>
  );
}

const UploadFiles = () => {
  const { setDataState, self_upload_type, active_date, model } = useContext(DataSourceContext);

  // Helper function to get file extension
  const getFileExtension = (fileName) => {
    return fileName?.toLowerCase().split('.').pop() || '';
  };

  // Helper function to get MIME type from file extension
  const getFileMimeType = (fileName) => {
    const extension = getFileExtension(fileName);
    const mimeTypes = {
      'csv': 'text/csv',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'pdf': 'application/pdf',
    };
    return mimeTypes[extension] || '';
  };

  // Helper function to validate file type by extension
  const isValidFileByExtension = (fileName, self_upload_type) => {
    const extension = getFileExtension(fileName);
    const csvXlsxTypes = ['xlsx_csv_waste', 'xlsx_csv_travelling'];
    
    if (csvXlsxTypes.includes(self_upload_type)) {
      return ['csv', 'xls', 'xlsx'].includes(extension);
    } else {
      return ['png', 'jpg', 'jpeg', 'pdf'].includes(extension);
    }
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      // Handle rejected files
      if (fileRejections && fileRejections.length > 0) {
        const invalidFiles = fileRejections.map(f => f.file.name).join(', ');
        setDataState((prev) => ({
          ...prev,
          alert: {
            ...prev.alert,
            open: true,
            severity: "error",
            alertTitle: "Invalid file type",
            alertText: `The following files are not accepted: ${invalidFiles}`,
            message: `Invalid file type: ${invalidFiles}`,
          },
        }));
      }
      
      // Process accepted files
      acceptedFiles.forEach((file) => {
        // Check file size
        if (file.size > MAX_FILE_SIZE) {
          setDataState((prev) => ({
            ...prev,
            alert: {
              ...prev.alert,
              open: true,
              severity: "error",
              alertTitle: "File too large",
              alertText: `File ${file.name} exceeds the 10 MB limit.`,
              message: `File ${file.name} exceeds the 10 MB limit.`,
            },
          }));
          return;
        }
        // Double check file extension as fallback
        if (!isValidFileByExtension(file.name, self_upload_type)) {
          setDataState((prev) => ({
            ...prev,
            alert: {
              ...prev.alert,
              open: true,
              severity: "error",
              alertTitle: "Invalid file type",
              alertText: `File ${file.name} is not a valid file type for the selected upload type.`,
              message: `File ${file.name} is not a valid file type for the selected upload type.`,
            },
          }));
          return;
        }

        const reader = new FileReader();

        reader.onloadend = () => {
          if (reader.result) {
            setDataState((prev) => ({
              ...prev,
              files: [
                ...prev.files,
                {
                  file,
                  file_name: file.name,
                  file_type: file.type || getFileMimeType(file.name),
                  size: file.size,
                  lastModified: file.lastModified,
                  isActive: false,
                },
              ],
            }));
          }
        };
        reader.readAsDataURL(file);
      });
    },
    [setDataState, self_upload_type]
  );

  // Determine accept types based on self_upload_type
  const acceptTypes = React.useMemo(() => {
    const csvXlsxTypes = ['xlsx_csv_waste', 'xlsx_csv_travelling'];
    if (csvXlsxTypes.includes(self_upload_type)) {
      return {
        "text/csv": [".csv"],
        "application/vnd.ms-excel": [".xls"],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      };
    } else {
      return {
        "image/*": [".png", ".jpg", ".jpeg"],
        "application/pdf": [".pdf"],
      };
    }
  }, [self_upload_type]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: acceptTypes,
    onDrop,
    multiple: true, // ✅ รองรับหลายไฟล์
  });
  
  return (
    <Box
      sx={{
        width: "100%",
        // height: isDragActive ? "100%" : "300px",
        height: (() => {
          if (isDragActive) return "100%";
          if (model === "agentic_ai") return "360px";
          if (self_upload_type === "bill") return "280px";
          return "250px";
        })(),
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        border: isDragActive ? "2px solid" : "none",
        borderColor: isDragActive ? theme.palette.primary.main : "transparent",
        backgroundColor: isDragActive ? theme.palette.primary.light : "white",
        borderRadius: "12px",
        padding: 1,
        position: "relative",
        marginTop: "12px",
        cursor: "pointer",
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <Box
        {...getRootProps()}
        sx={{
          border: "1px dashed",
          borderColor: "#E0E3E7",
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          objectFit: "cover",
          borderRadius: "10px",
          flexDirection: "column",
        }}
      >
        <input {...getInputProps()} />
        <UploadOutlinedIcon
          sx={{ fontSize: 25, color: theme.palette.primary.dark }}
        />
        <Typography variant="h6">
          {isDragActive
            ? "Drop file(s) here..."
            : "Drag and drop file(s) here or click to upload"}
        </Typography>
        <Typography variant="caption" sx={{ mt: 1, color: "text.secondary" }}>
          {['xlsx_csv_waste', 'xlsx_csv_travelling'].includes(self_upload_type)
            ? "Accepted formats: CSV, XLS, XLSX"
            : "Accepted formats: PNG, JPG, JPEG, PDF"}
        </Typography>
      </Box>
    </Box>
  );
};

const Self_upload_type = [
  { display_name: 'Bills', type: 'bill', icon: <DescriptionIcon sx={{ fontSize: 18 }}/>, status: 'Sync' },
  { display_name: 'Water Meters', type: 'water_meter', icon: <CameraAltIcon sx={{ fontSize: 18 }}/>, status: 'Sync' },
  { display_name: 'Waste', type: 'xlsx_csv_waste', icon: <DescriptionIcon sx={{ fontSize: 18 }}/>, status: 'Sync' },
  { display_name: 'Commuting', type: 'xlsx_csv_travelling', icon: <DescriptionIcon sx={{ fontSize: 18 }}/>, status: 'Sync' },
]

const TypeSelection = () => {
  const { selectServer, self_upload_type, setDataState } = useContext(DataSourceContext);
  if (selectServer?.name === "Self Upload" || selectServer?.name === "Dap Lite") {
  return (
    <Stack direction="row" spacing={1} 
    // sx={{ 
    //   padding: '12px', 
    //   borderRadius: '12px', 
    //   backgroundColor: 'white',
    //   boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    // }}
    >
      {Self_upload_type?.map((item) => (
        <CustomChip
          label={item.display_name}
          status={item.status}
          // icon={item.icon}
          isSelected={self_upload_type === item.type}
          onClick={() => {
            // Determine Select_model based on type
            const getSelectModel = () => {
              const type = item.type;
              if (type === 'water_meter') {
                return Water_meter_model;
              } else if (type === 'xlsx_csv_waste') {
                return Waste_model;
              } else if (type === 'xlsx_csv_travelling') {
                return Commuting_model;
              } else {
                return General_model;
              }
            };
            
            const selectedModel = getSelectModel();
            // Set model to the first model's value as default
            const defaultModel = selectedModel?.[0]?.value || null;
            
            setDataState((prevState) => ({
              ...prevState,
              model: defaultModel,
              self_upload_type: item.type,
              Select_model: selectedModel,
            }));
          }}
        />
    ))}
    </Stack>
    );
  }else return null;
};

const ModelSelection = () => {
  const { setDataState, model, self_upload_type, Select_model } = useContext(DataSourceContext);
  const [checked, setChecked] = useState(true);

  useEffect(() => {
    if (checked) {
      setDataState((prev) => ({ ...prev, model: 'agentic_ai' }));
    }
  }, []); 

  const handleChange = (event) => {
    const isChecked = event.target.checked;
    setChecked(isChecked);
    if (isChecked) {
      setDataState((prev) => ({ ...prev, model: 'agentic_ai' }));
    } else {
      const defaultModel = Select_model?.[0]?.value || 'general';
      setDataState((prev) => ({ ...prev, model: defaultModel }));
    }
  };


  const handleModelChange = (event, newValue) => {
    setDataState((prev) => ({ ...prev, model: newValue?.value }));
  };

  const handleValueChange = (newValue) => {
    setDataState((prev) => ({ ...prev, active_date: newValue }));
  };

  return (
    <Stack direction="row" spacing={1} sx={{ 
      width: "100%", 
      marginTop: '12px',
      backgroundColor: 'white', 
      padding: '12px', 
      borderRadius: '12px', 
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {self_upload_type === 'bill' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={checked}
                  onChange={handleChange}
                  sx={{
                    width: 38,
                    height: 22,
                    padding: 0,
                    borderRadius: '10px',
                    '& .MuiSwitch-switchBase': {
                      padding: 0,
                      margin: 0,
                      transitionDuration: '300ms',
                      '&.Mui-checked': {
                        transform: 'translateX(16px)',
                        color: '#fff',
                        '& + .MuiSwitch-track': {
                          backgroundColor: '#65C466',
                          opacity: 1,
                          border: 0,
                        },
                        '&.Mui-disabled + .MuiSwitch-track': {
                          opacity: 0.5,
                        },
                      },
                      '&.Mui-focusVisible .MuiSwitch-thumb': {
                        color: '#33cf4d',
                        border: '6px solid #fff',
                      },
                      '&.Mui-disabled .MuiSwitch-thumb': {
                        color: theme.palette.grey[100],
                      },
                      '&.Mui-disabled + .MuiSwitch-track': {
                        opacity: 0.7,
                      },
                    },
                    '& .MuiSwitch-thumb': {
                      boxSizing: 'border-box',
                      width: 18,
                      height: 18,
                      marginTop: '2px',
                    },
                    '& .MuiSwitch-track': {
                      borderRadius: 26 / 2,
                      backgroundColor: '#E9E9EA',
                      opacity: 1,
                      transition: theme.transitions.create(['background-color'], {
                        duration: 500,
                      }),
                    },
                  }}
                />
              }
              label={<Typography variant="h7" sx={{ fontWeight: 500, ml: -0.5 }}>Agentic OCR</Typography>}
              labelPlacement="start"
              sx={{ ml: 0, gap: 0, width: '100%', justifyContent: 'space-between' }}
            />
          </Box>
        )}
        
        {(self_upload_type === 'bill' && !checked) && (
          <hr/>
        )}

        {!(self_upload_type === 'bill' && checked) && (
          <MyAutocomplete
            sx={{ flex: 1, mt: self_upload_type === 'bill' ? 0.5 : 0 }}
            placeholder="Select Model"
            options={Select_model}
            value={Select_model?.find((item) => item.value === model) || null}
            getOptionLabel={(option) => option.name}
            onChange={handleModelChange}
            disableClearable
            readOnly
          />
        )}

      </Box>
      {self_upload_type === 'water_meter' && (
        <DatePickerYearMonth
          disabled={model === "fleetcard"}
          setValue={handleValueChange}
        />
      )}
    </Stack>
  );
};

const DownloadTemplateSection = () => {
  const { self_upload_type } = useContext(DataSourceContext);

  const showDownloadSection = self_upload_type === 'xlsx_csv_waste' || self_upload_type === 'xlsx_csv_travelling';

  if (!showDownloadSection) {
    return null;
  }

  const getTemplateFileName = () => {
    if (self_upload_type === 'xlsx_csv_waste') {
      return 'Waste_Template_VEKIN.xlsx';
    } else if (self_upload_type === 'xlsx_csv_travelling') {
      return 'Employee_Commuting_Template_VEKIN.xlsx';
    }
    return 'template.xlsx';
  };

  const getTemplatePath = () => {
    if (self_upload_type === 'xlsx_csv_waste') {
      return '/Waste_Template_VEKIN.xlsx';
    } else if (self_upload_type === 'xlsx_csv_travelling') {
      return '/Employee_Commuting_Template_VEKIN.xlsx';
    }
    return '/template.xlsx';
  };

  return (
    <Box
      sx={{
        marginTop: '12px',
        padding: '12px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      }}
    >
      <Typography
        variant="body1"
        sx={{
          color: "black",
          marginBottom: 1.5,
          fontWeight: 600,
        }}
      >
        Download Template
      </Typography>
      <Box
        component="a"
        href={getTemplatePath()}
        download={getTemplateFileName()}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          padding: 2,
          backgroundColor: "#FAFAFA",
          border: "1px dashed #E0E3E7",
          borderRadius: '8px',
          cursor: "pointer",
          textDecoration: "none",
          width: "40%",
          transition: 'all 0.2s ease-in-out',
          "&:hover": {
            backgroundColor: "#F0F0F0",
            borderColor: theme.palette.primary.main,
            transform: 'translateY(-2px)',
          },
        }}
      >
        {/* <InsertDriveFileOutlinedIcon
          sx={{
            color: theme.palette.success.main,
            fontSize: 40,
          }}
        /> */}
        <img
          src="/excel-icon.png"
          alt="excel icon"
          style={{ width: 56, height: 56 }}
        />
        <Typography
          variant="body1"
          sx={{
            marginTop: 1,
            textAlign: "center",
            color: theme.palette.text.primary,
            fontWeight: 500,
            maxWidth: "100%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {getTemplateFileName()}
        </Typography>
      </Box>
    </Box>
  );
};

const FileItem = ({ file_type, file_name }) => {
  let displayType = file_type;
  let color = theme.palette.primary.main;
  if (file_type === "application/pdf") {
    displayType = "PDF";
    color = theme.palette.error.main;
  }
  if (file_type === "image/png") {
    displayType = "PNG";
    color = theme.palette.warning.main;
  }
  if (file_type === "image/jpeg") {
    displayType = "JPEG";
    color = "orange";
  }
  if (file_type === "image/jpg") {
    displayType = "JPG";
    color = "orange";
  }
  // Handle CSV and Excel files
  if (file_type === "text/csv" || (file_name && file_name.toLowerCase().endsWith('.csv'))) {
    displayType = "CSV";
    color = theme.palette.success.main;
  }
  if (file_type === "application/vnd.ms-excel" || (file_name && file_name.toLowerCase().endsWith('.xls'))) {
    displayType = "XLS";
    color = theme.palette.info.main;
  }
  if (file_type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || (file_name && file_name.toLowerCase().endsWith('.xlsx'))) {
    displayType = "XLSX";
    color = theme.palette.info.main;
  }
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: 50,
        height: 50,
        position: "relative",
      }}
    >
      <InsertDriveFileOutlinedIcon
        sx={{ color: theme.palette.action.disabled, fontSize: 50 }}
      />
      <Typography
        sx={{
          fontSize: 8,
          color: "white",
          position: "absolute",
          top: 15,
          left: 3,
          backgroundColor: color,
          borderRadius: 0.5,
          padding: 0.5,
        }}
      >
        {displayType}
      </Typography>
    </Box>
  );
};

const FileList = () => {
  const [view, setView] = React.useState("grid");
  const [selectedFiles, setSelectedFiles] = React.useState(false);
  const { files, setDataState, uploadTransaction, model } =
    useContext(DataSourceContext);

  const handleCheckbox = (index) => {
    const newFiles = files;
    newFiles[index].isActive = !newFiles[index].isActive;
    setDataState((prev) => ({ ...prev, files: newFiles }));
    // newFiles.filter((file) => file.isActive).length === 0 ? setSelectedFiles(false) : setSelectedFiles(true);
  };

  // const handleDelete = (index) => {
  //   const newFiles = files.filter((file) => file.isActive);
  //   setDataState((prev) => ({ ...prev, files: newFiles }));
  // };

  const handleDeleteFile = (index) => {
    const newFiles = files;
    newFiles[index].isActive = !newFiles[index].isActive;
    setDataState((prev) => ({
      ...prev,
      files: newFiles,
      warning: {
        ...prev.warning,
        open: true,
        severity: "error",
        warningTitle: "Confirm delete",
        warningText: null,
        handleOkClicked: () => {
          const newFiles = files.filter((file) => !file.isActive);
          setDataState((prev) => ({
            ...prev,
            files: newFiles,
            warning: {
              ...prev.warning,
              open: false,
            },
          }));
        },
        handleCancle: () =>
          setDataState((prev) => ({
            ...prev,
            warning: { ...prev.warning, open: false },
          })),
        okBtnText: "Delete",
      },
    }));
  };

  const handleDeleteAllFile = () => {
    setDataState((prev) => ({
      ...prev,
      warning: {
        ...prev.warning,
        open: true,
        warningTitle: "Confirm delete",
        warningText: null,
        severity: "error",
        handleOkClicked: () => {
          setDataState((prev) => ({
            ...prev,
            files: [],
            warning: { ...prev.warning, open: false },
          }));
        },
        handleCancle: () =>
          setDataState((prev) => ({
            ...prev,
            warning: { ...prev.warning, open: false },
          })),
        okBtnText: "Delete",
      },
    }));
  };

  const formatSize = (size) => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  // const handleSelectAll = () => {
  //   if (files?.filter((f) => f.isActive)?.length === files?.length) {
  //       const newFiles = files.map((file) => ({ ...file, isActive: false }));
  //       setDataState((prev) => ({ ...prev, files: newFiles }));
  //   } else {
  //       const newFiles = files.map((file) => ({ ...file, isActive: true }));
  //       setDataState((prev) => ({ ...prev, files: newFiles }));
  //   }
  // };

  const handleCancel = () => {
    setDataState((prev) => ({
      ...prev,
      files: files.map((file) => ({ ...file, isActive: false })),
    }));
    setSelectedFiles(false);
  };

  const saveDelete = () => {
    const newFiles = files.filter((file) => !file.isActive);
    setDataState((prev) => ({
      ...prev,
      files: newFiles,
      warning: { ...prev.warning, open: false },
    }));
    setSelectedFiles(false);
  };

  const handleDelete = () => {
    setDataState((prev) => ({
      ...prev,
      warning: {
        ...prev.warning,
        open: true,
        warningTitle: "Confirm delete",
        warningText: null,
        handleOkClicked: saveDelete,
        handleCancle: () =>
          setDataState((prev) => ({
            ...prev,
            warning: { ...prev.warning, open: false },
          })),
        okBtnText: "Delete",
      },
    }));
  };

  const handleCancelUpload = () => {
    setDataState((prev) => ({
      ...prev,
      files: [],
      dialog: {
        ...prev.dialog,
        upload_transaction: false,
      },
    }));
  };

  const handleUpload = () => {
    // Validate required fields
    if (!model) {
      setDataState((prev) => ({
        ...prev,
        alert: {
          ...prev.alert,
          open: true,
          severity: "error",
          alertTitle: "Missing required field",
          alertText: "Please select a model before uploading files.",
          message: "Please select a model before uploading files.",
        },
      }));
      return;
    }

    if (files.length === 0) {
      setDataState((prev) => ({
        ...prev,
        alert: {
          ...prev.alert,
          open: true,
          severity: "error",
          alertTitle: "No files selected",
          alertText: "Please select at least one file to upload.",
          message: "Please select at least one file to upload.",
        },
      }));
      return;
    }

    setDataState((prev) => ({
      ...prev,
      loading: {
        ...prev.loading,
        modal: true,
      },
    }));
    uploadTransaction();
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        position: "relative",
      }}
    >
      {files?.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "center",
            paddingBottom: "10px",
            marginTop: '10px',
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: theme.palette.primary.dark,
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={handleDeleteAllFile}
            >
              Delete all
            </Typography>
            {/* {selectedFiles ? <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox size="small" checked={files?.filter((f) => f.isActive)?.length === files?.length} onChange={handleDeleteAllFile}/>
                    <Typography variant="h5" sx={{ color: theme.palette.primary.dark }}>Select all</Typography>
                </Box> : <Typography variant="h5" sx={{ color: theme.palette.primary.dark, margin: '10px 0px 0px 0px', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => files?.length > 0 && setSelectedFiles(true)}>Select files</Typography>} */}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {selectedFiles && (
              <Button variant="outlined" size="small" onClick={handleCancel}>
                Cancel
              </Button>
            )}
            {/* {files?.filter((f) => f.isActive)?.length > 0 &&
                <Button size='small' variant="contained" color='error' onClick={handleDelete}>Delete</Button>
              } */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                backgroundColor: "#f5f5f5",
                padding: "2px",
                borderRadius: "20px",
                border: "1px solid",
                borderColor: "#E0E3E7",
              }}
            >
              <FormatListBulletedIcon
                sx={{
                  color: view === "list" ? "white" : theme.palette.primary.dark,
                  fontSize: 25,
                  backgroundColor:
                    view === "list" ? theme.palette.primary.dark : "white",
                  padding: "2px",
                  borderRadius: "20px",
                  border: "1px solid",
                  borderColor: "#E0E3E7",
                  cursor: "pointer",
                }}
                onClick={() => setView("list")}
              />
              <AppsOutlinedIcon
                sx={{
                  color: view === "grid" ? "white" : theme.palette.primary.dark,
                  fontSize: 25,
                  backgroundColor:
                    view === "grid" ? theme.palette.primary.dark : "white",
                  padding: "2px",
                  borderRadius: "20px",
                  border: "1px solid",
                  borderColor: "#E0E3E7",
                  cursor: "pointer",
                }}
                onClick={() => setView("grid")}
              />
            </Box>
          </Box>
        </Box>
      )}
      {files?.length > 0 && (
        <Grid container spacing={2}>
          {files.map((file, index) => {
            return (
              <Grid item xs={view === "list" ? 6 : 3} key={index}>
                <Box
                  sx={{
                    border: "1px solid #E0E3E7",
                    padding: 1,
                    borderRadius: 2,
                    position: "relative",
                    display: "flex",
                    flexDirection: view === "list" ? "row" : "column",
                    justifyContent:
                      view === "list" ? "space-between" : "center",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {selectedFiles && (
                    <Checkbox
                      checked={file.isActive}
                      size="small"
                      sx={{
                        position: view === "grid" ? "absolute" : "relative",
                        top: 0,
                        left: 0,
                      }}
                      onChange={() => handleCheckbox(index)}
                    />
                  )}
                  <FileItem file_type={file?.file_type || null} file_name={file?.file_name || null} />
                  <Box sx={{ width: "100%" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: view === "list" ? 150 : 90,
                      }}
                    >
                      {file.file_name}
                    </Typography>
                    <Typography sx={{ fontSize: 12 }}>
                      {file.size && formatSize(file.size)}
                    </Typography>
                    <Typography sx={{ fontSize: 12 }}>
                      {new Date(file.lastModified).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <IconButton
                    sx={{ position: "absolute", top: -15, right: -15 }}
                  >
                    <CancelIcon
                      sx={{
                        color: theme.palette.error.main,
                        fontSize: 25,
                        cursor: "pointer",
                      }}
                      onClick={() => handleDeleteFile(index)}
                    />
                  </IconButton>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
      <Box
        sx={{
          // position: "absolute",
          // bottom: '14px',
          left: 0,
          display: "flex",
          gap: 2,
          width: "100%",
          padding: 1,
          backgroundColor:
            files.filter((file) => file.isActive).length > 0
              ? "white"
              : "transparent",
          justifyContent: "center",
          borderTop:
            files.filter((file) => file.isActive).length > 0
              ? "1px solid #E0E3E7"
              : "none",
        }}
      >
        {!selectedFiles && (
          <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'center', marginTop: '4px' }}>
            <Button
              variant="outlined"
              onClick={handleCancelUpload}
              sx={{ width: "40%", height: "40px" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              sx={{ width: "40%", height: "40px" }}
              disabled={files.length === 0}
            >
              Confirm Upload{" "}
              {files?.length > 0 &&
                (files.length > 1
                  ? `${files.length} files`
                  : `${files.length} file`)}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const Contents = () => {
  return (
    <Box sx={{ 
      height: "100%", 
      paddingRight: "20px",
      paddingBottom: "16px",
    }}>
      <TypeSelection />
      <ModelSelection />
      <UploadFiles />
      <DownloadTemplateSection />
      <FileList />
    </Box>
  );
};
